
import React from 'react';
import SEOHead from '@/components/SEOHead';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Users, MessageCircle, Calendar, Lock, CheckCircle } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUserRole } from '@/hooks/useUserRole';
import { toast } from 'sonner';

const Community = () => {
  const isMobile = useIsMobile();
  const { permissions } = useUserRole();

  // Define section access based on role
  const getSectionAccess = (section: string) => {
    const role = permissions.role;
    
    switch (section) {
      case 'competitions':
        // All users can view competitions, but different access levels
        return {
          hasAccess: true,
          route: '/games',
          restrictions: role === 'support' ? 'Score games and manage competitions' : 'Join and view competitions'
        };
      case 'teams':
        // Teams access based on role
        return {
          hasAccess: true,
          route: '/teams',
          restrictions: ['coach', 'team-owner'].includes(role) ? 'Create and manage teams' 
            : role === 'support' ? 'View teams and provide scoring support'
            : 'Join teams and view team information'
        };
      case 'lockeroom':
        // Chat access for all authenticated users
        return {
          hasAccess: true,
          route: '/chat',
          restrictions: role === 'support' ? 'Moderate chat and provide support'
            : 'Chat with teammates and community'
        };
      case 'events':
        // Events access for all users
        return {
          hasAccess: true,
          route: '/game-stats',
          restrictions: role === 'support' ? 'Score events and manage statistics'
            : permissions.canCreateGames ? 'Create and manage events'
            : 'View and participate in events'
        };
      default:
        return { hasAccess: false, route: '/', restrictions: 'No access' };
    }
  };

  const handleSectionClick = (section: string) => {
    const access = getSectionAccess(section);
    if (!access.hasAccess) {
      toast.error('You do not have access to this section');
      return;
    }
    // Navigation will be handled by Link component
  };

  const renderSectionCard = (
    section: string,
    icon: React.ReactNode,
    title: string,
    description: string
  ) => {
    const access = getSectionAccess(section);
    
    return (
      <Card 
        className={`bg-gray-800 border-gray-700 hover:border-sports-green transition-colors cursor-pointer ${
          access.hasAccess ? 'opacity-100' : 'opacity-60'
        }`}
        onClick={() => handleSectionClick(section)}
      >
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            {icon}
            {permissions.role === 'support' && (
              <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                Score Keeper
              </Badge>
            )}
          </div>
          <CardTitle className="text-white">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-gray-300 text-sm text-center">
            {description}
          </p>
          <div className="text-xs text-center">
            <Badge variant="outline" className="text-sports-green border-sports-green/50">
              {access.restrictions}
            </Badge>
          </div>
          {access.hasAccess ? (
            <Link to={access.route}>
              <Button className="w-full bg-sports-green text-black hover:bg-sports-green/90 mt-2">
                <CheckCircle className="h-4 w-4 mr-2" />
                Access {title}
              </Button>
            </Link>
          ) : (
            <Button disabled className="w-full mt-2">
              <Lock className="h-4 w-4 mr-2" />
              Restricted Access
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      <SEOHead title="Community" description="Join the Players Lockeroom sports community — connect with athletes, coaches, and fans." path="/community" />
      {/* Desktop Header */}
      {!isMobile && <Header />}
      {/* Mobile Header */}
      {isMobile && <MobileHeader />}
      
      <main className="flex-grow pt-20 md:pt-28 py-6 md:py-12 px-4 md:px-6 pb-20 md:pb-6 w-full">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Welcome to the Players Lockeroom Community
            </h1>
            <p className="text-gray-300 text-lg">
              Connect, compete, and grow with fellow athletes
            </p>
            {permissions.role === 'support' && (
              <div className="mt-4">
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-lg px-4 py-2">
                  Score Keeper Account - Special Access & Responsibilities
                </Badge>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {renderSectionCard(
              'competitions',
              <Trophy className="h-8 w-8 text-sports-green" />,
              'Competitions',
              'Join tournaments and showcase your skills'
            )}

            {renderSectionCard(
              'teams',
              <Users className="h-8 w-8 text-sports-green" />,
              'Teams',
              'Find your team or create a new one'
            )}

            {renderSectionCard(
              'lockeroom',
              <MessageCircle className="h-8 w-8 text-sports-green" />,
              'Lockeroom',
              'Chat with teammates and fellow players'
            )}

            {renderSectionCard(
              'events',
              <Calendar className="h-8 w-8 text-sports-green" />,
              'Events',
              'Stay updated on upcoming events'
            )}
          </div>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                Community Feed
                {permissions.role === 'support' && (
                  <Badge className="bg-yellow-500/20 text-yellow-400">
                    Score Keeper View
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-300">
                  Welcome to your community hub! This is where you'll see updates from your teams, 
                  upcoming events, and connect with other players in the lockeroom.
                </p>
                
                {permissions.role === 'support' && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-400 mb-2">Score Keeper Responsibilities:</h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Manage game scores and statistics</li>
                      <li>• Moderate community interactions</li>
                      <li>• Provide support to teams and players</li>
                      <li>• Ensure fair play and accurate scoring</li>
                      <li>• Help with technical issues during events</li>
                    </ul>
                  </div>
                )}

                {permissions.canCreateGames && (
                  <div className="bg-sports-green/10 border border-sports-green/20 rounded-lg p-4">
                    <h4 className="font-semibold text-sports-green mb-2">Management Tools:</h4>
                    <div className="flex flex-wrap gap-2">
                      <Link to="/games">
                        <Button size="sm" variant="outline" className="border-sports-green text-sports-green">
                          Manage Games
                        </Button>
                      </Link>
                      {permissions.canManageTeams && (
                        <Link to="/teams">
                          <Button size="sm" variant="outline" className="border-sports-green text-sports-green">
                            Manage Teams
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      {/* Desktop Footer */}
      {!isMobile && <Footer />}
      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default Community;
